import {
  Button,
  Card,
  CardContent,
  Icon,
  Input,
  Label,
  Skeleton,
  Stack,
} from '@hurdl/design-system';
import { useParams } from '@tanstack/react-router';
import { Check, Pencil, Plus, Trash2, X } from 'lucide-react';
import { useEffect, useId, useState, type ReactElement } from 'react';

import { type Category } from '../bespoke/menu-workspace/types.js';
import { restaurantLabelOf, useMenuOperations } from './menus-wiring.js';
import { useToast } from './toast-context.js';

/**
 * The managed Categories of one Restaurant (menus-spec R6.3): the groupings a reviewer
 * curates and a categorisation chooses from, listed and edited in one place.
 *
 * Categories belong to a Restaurant — one managed set each — so this page reads the
 * Restaurant from the route (`.../menus/$restaurantKey/categories`) rather than being a
 * single panel-wide page pinned to one Restaurant. It loads that Restaurant's managed
 * Categories, lists each as a row with its name and the two acts a Category takes — a
 * rename in place and a removal — and offers an "Add category" field at the top. Each
 * act calls the composed operation, reports the outcome in a toast, and reloads the list
 * so the page shows what the backend holds rather than an optimistic guess. Skeletons
 * stand in while the read is in flight, and an empty set says so rather than showing
 * nothing.
 *
 * Mobile-first: one column, generous tap targets, the add field and each row full
 * width, no horizontal overflow. The one `h1` is the page's; the `main` landmark is the
 * Layout's. Every act resolves against the route tree or the composed operations, so no
 * address literal reaches this source.
 */
export function CategoriesPage(): ReactElement {
  const { restaurantKey: routeRestaurantKey } = useParams({ strict: false });
  const restaurantKey = routeRestaurantKey ?? '';
  const operations = useMenuOperations();
  const { notify } = useToast();

  const [categories, setCategories] = useState<
    ReadonlyArray<Category> | undefined
  >(undefined);
  const [adding, setAdding] = useState(false);
  const addFieldId = useId();
  const [draftName, setDraftName] = useState('');

  const reload = (): void => {
    void operations.listCategories(restaurantKey).then(setCategories);
  };

  useEffect(() => {
    let live = true;
    void operations.listCategories(restaurantKey).then((next) => {
      if (live) setCategories(next);
    });
    return () => {
      live = false;
    };
  }, [operations, restaurantKey]);

  const add = (): void => {
    const name = draftName.trim();
    if (name === '' || adding) return;
    setAdding(true);
    void operations
      .createCategory(restaurantKey, name)
      .then(() => {
        notify('success', `Added ${name}`);
        setDraftName('');
        reload();
      })
      .catch((error: unknown) => {
        notify('destructive', 'Category not added', messageOf(error));
      })
      .finally(() => {
        setAdding(false);
      });
  };

  const rename = (category: Category, name: string): void => {
    void operations
      .renameCategory(category.key, name)
      .then(() => {
        notify('success', `Renamed to ${name}`);
        reload();
      })
      .catch((error: unknown) => {
        notify('destructive', 'Category not renamed', messageOf(error));
      });
  };

  const remove = (category: Category): void => {
    void operations
      .removeCategory(category.key)
      .then(() => {
        notify('success', `Removed ${category.name}`);
        reload();
      })
      .catch((error: unknown) => {
        notify('destructive', 'Category not removed', messageOf(error));
      });
  };

  return (
    <Stack gap="large">
      <Stack gap="small">
        <h1 className="font-hurdl-display text-hurdl-heading font-hurdl-semibold">
          {restaurantLabelOf(restaurantKey)} categories
        </h1>
        <p className="text-hurdl-body text-muted-foreground">
          The managed groupings for {restaurantLabelOf(restaurantKey)}, chosen
          when a reviewer categorises an item.
        </p>
      </Stack>

      <Card className="panel-glass">
        <CardContent>
          <Stack
            direction="responsive"
            gap="small"
            align="end"
            className="w-full"
          >
            <Stack gap="small" className="min-w-0 grow">
              <Label htmlFor={addFieldId}>Add category</Label>
              <Input
                id={addFieldId}
                value={draftName}
                placeholder="New category name"
                onChange={(event) => {
                  setDraftName(event.target.value);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    add();
                  }
                }}
              />
            </Stack>
            <Button
              variant="primary"
              disabled={draftName.trim() === '' || adding}
              onClick={add}
            >
              <Icon icon={Plus} size="small" />
              Add
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {categories === undefined ? (
        <Stack gap="small" aria-busy="true">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </Stack>
      ) : categories.length === 0 ? (
        <p className="text-hurdl-muted-foreground">
          No categories yet. Add one above.
        </p>
      ) : (
        <Stack gap="small">
          {categories.map((category) => (
            <CategoryRow
              key={category.key}
              category={category}
              onRename={(name) => {
                rename(category, name);
              }}
              onRemove={() => {
                remove(category);
              }}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
}

/**
 * One Category as a row: its name, an edit control that turns the name into an input in
 * place, and a remove control. The edit's confirm commits the rename; its cancel
 * restores the held name.
 */
function CategoryRow({
  category,
  onRename,
  onRemove,
}: {
  readonly category: Category;
  readonly onRename: (name: string) => void;
  readonly onRemove: () => void;
}): ReactElement {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(category.name);
  const fieldId = useId();

  const commit = (): void => {
    const name = draft.trim();
    if (name === '' || name === category.name) {
      setEditing(false);
      setDraft(category.name);
      return;
    }
    setEditing(false);
    onRename(name);
  };

  const cancel = (): void => {
    setEditing(false);
    setDraft(category.name);
  };

  return (
    <Card className="panel-glass">
      <CardContent>
        <Stack direction="horizontal" gap="small" align="centre">
          {editing ? (
            <>
              <Label htmlFor={fieldId} className="sr-only">
                Category name
              </Label>
              <Input
                id={fieldId}
                value={draft}
                autoFocus
                className="min-w-0 grow"
                onChange={(event) => {
                  setDraft(event.target.value);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    commit();
                  } else if (event.key === 'Escape') {
                    event.preventDefault();
                    cancel();
                  }
                }}
              />
              <Button
                variant="primary"
                aria-label={`Save ${category.name}`}
                onClick={commit}
              >
                <Icon icon={Check} size="small" />
              </Button>
              <Button
                variant="secondary"
                aria-label="Cancel rename"
                onClick={cancel}
              >
                <Icon icon={X} size="small" />
              </Button>
            </>
          ) : (
            <>
              <span className="min-w-0 grow truncate text-hurdl-subheading">
                {category.name}
              </span>
              <Button
                variant="secondary"
                aria-label={`Rename ${category.name}`}
                onClick={() => {
                  setDraft(category.name);
                  setEditing(true);
                }}
              >
                <Icon icon={Pencil} size="small" animateOnHover />
              </Button>
              <Button
                variant="destructive"
                aria-label={`Remove ${category.name}`}
                onClick={onRemove}
              >
                <Icon icon={Trash2} size="small" />
              </Button>
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

/** The message of a thrown refusal, or a fallback where it carries none. */
function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : 'Please try again.';
}
